import type {
  CostRule,
  CostTerm,
  CreditEstimate,
  NodeMetrics,
  PipelineNode,
  PriceBook,
  PricedPlan,
} from "@loumy/contracts";
import { FANOUT_COUNT_KEYS, firstNumericParam, UNIT_PARAM_KEYS } from "./metrics";

// §3.6 Pricing-Engine über den AUFGELÖSTEN Plan (D17/D31/D34/D39/D44) ───────────
//
//   price(plan, priceBook) = toolBaseFee[plan.toolId]
//     + Σ über plan.spec.nodes:  evalRule(rule, metriken, params) × (fanOut ? menge : 1)
//
// • Versions-Pinning (D31): Schlüssel 'id@version' schlägt 'id'.
// • Provider-Pinning (D101): trägt der Knoten einen eingefrorenen `providerId`-Param,
//   schlagen provider-bewusste Schlüssel die generischen — Kette:
//   'id@version#provider' → 'id#provider' → 'id@version' → 'id'. Modelle mit eigenen
//   COGS bekommen so eigene Preise, ohne die Capability-Version zu forken.
// • Rundung: `ceil` JE CostTerm (D44) — schärfer als das gröbere §3.6-`ceil(node)`;
//   da jeder Term ganzzahlig wird, ist die Knoten-Summe ohnehin integer.
// • Bedingte Knoten: WORST-CASE-Hold (D17/D36) — `price` zählt JEDEN Knoten, auch
//   bedingte (als ob sie laufen). Übersprungene Knoten erstattet später `release_hold`.
//   Pricing ist damit der Worst-Case-Estimate für `estimated_credits` (§9.5).
// • FAIL-CLOSED (D44, Codex-P1): Der Estimate ist die Hold-Basis. Lieber WERFEN als
//   zu niedrig schätzen (Hold-Unterdeckung = Ledger-Loch). Daher Default:
//     - unbekannte CostRule          → throw (außer onUnpriced:'zero')
//     - aktiver Term ohne Metrik      → throw
//     - `when`-Param kein Literal      → throw (Plan nicht eingefroren, D34)
//     - fanOut ohne gefrorene menge    → throw

export class PricingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PricingError";
  }
}

export interface PriceOptions {
  /** Ersetzt die Default-Metrik-Konvention (metrics.ts) vollständig. */
  metricsFor?: (node: PipelineNode) => NodeMetrics;
  /** Ersetzt die Default-Fan-out-Kardinalität. */
  fanOutCountFor?: (node: PipelineNode) => number;
  /**
   * Verhalten bei fehlender CostRule. Default `'throw'` (fail-closed). `'zero'` nur,
   * wenn der Aufrufer fehlende Einträge bewusst als kostenlos akzeptiert (Tests; §13-Free-Tool).
   */
  onUnpriced?: "throw" | "zero";
}

/**
 * CostRule für (id, version[, providerId]) — Fallback-Kette (D31/D101):
 * 'id@version#provider' → 'id#provider' → 'id@version' → 'id'. Ohne `providerId`
 * (kein Pin im Knoten / modell-loses Tool) exakt die historische D31-Kette.
 */
export function resolveCostRule(
  book: PriceBook,
  id: string,
  version: number,
  providerId?: string,
): CostRule | undefined {
  const keys =
    providerId !== undefined && providerId !== ""
      ? [`${id}@${version}#${providerId}`, `${id}#${providerId}`, `${id}@${version}`, id]
      : [`${id}@${version}`, id];
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(book.capabilityCost, k)) return book.capabilityCost[k];
  }
  return undefined;
}

/** `when`-Prädikat (D39): nur Literal-params vergleichbar; ScalarRef ⇒ Plan nicht eingefroren (D34). */
function termActive(term: CostTerm, params: Record<string, unknown>, tag: string): boolean {
  if (!term.when) return true;
  const key = term.when.param;
  const v = Object.prototype.hasOwnProperty.call(params, key) ? params[key] : undefined;
  if (typeof v === "object" && v !== null)
    throw new PricingError(
      `${tag}: when-Param '${key}' ist kein Literal (unaufgelöste ScalarRef → Plan nicht eingefroren, D34).`,
    );
  return v === term.when.eq;
}

/** endlich & ≥ 0? (gemeinsamer fail-closed-Guard für alle Preis-Eingaben, D44). */
function nonNegFinite(v: number): boolean {
  return Number.isFinite(v) && v >= 0;
}

/**
 * Metrik für einen Term — STRIKT (fail-closed). Auch der Override-Pfad wird geprüft
 * (Codex-P1): eine FEHLENDE Unit im Override ⇒ wirft (nicht still 0); negativ/NaN ⇒ wirft.
 * Explizites `0` im Override ist erlaubt (bewusste Aufrufer-Entscheidung).
 */
function metricStrict(
  unit: CostTerm["unit"],
  params: Record<string, unknown>,
  override: NodeMetrics | undefined,
  tag: string,
): number {
  if (unit === "flat") return 1; // 'flat' zählt konstant einmal — Override irrelevant
  if (override) {
    const v = override[unit];
    if (v === undefined || !nonNegFinite(v))
      throw new PricingError(`${tag}: Override-Metrik '${unit}' fehlt oder ist ungültig (${v}) — fail-closed (D44).`);
    return v;
  }
  const v = firstNumericParam(params, UNIT_PARAM_KEYS[unit]);
  if (v === undefined)
    throw new PricingError(
      `${tag}: aktiver CostTerm braucht Metrik '${unit}', aber kein numerischer Param ` +
        `(${UNIT_PARAM_KEYS[unit].join("/")}) im eingefrorenen Plan (D34/D44).`,
    );
  if (v < 0) throw new PricingError(`${tag}: Metrik '${unit}' ist negativ (${v}).`);
  return v;
}

/**
 * Wertet eine CostRule für EINEN Knoten aus (vor Fan-out-Multiplikation).
 * `override` = von der Capability beigesteuerte Metriken (umgeht die Param-Konvention).
 * Negative/nicht-endliche Book-Werte (number-Rule, perUnit) ⇒ wirft (Codex-P1, D44).
 */
export function evalCostRule(
  rule: CostRule,
  params: Record<string, unknown>,
  override?: NodeMetrics,
  tag = "node",
): number {
  if (typeof rule === "number") {
    if (!nonNegFinite(rule)) throw new PricingError(`${tag}: ungültige CostRule ${rule} (muss endlich, ≥0 sein, D44).`);
    return Math.ceil(rule); // flat-Pauschale
  }
  let sum = 0;
  for (const term of rule.sum) {
    if (!termActive(term, params, tag)) continue;
    if (!nonNegFinite(term.perUnit))
      throw new PricingError(`${tag}: ungültiger perUnit ${term.perUnit} (muss endlich, ≥0 sein, D44).`);
    const c = Math.ceil(term.perUnit * metricStrict(term.unit, params, override, tag)); // ceil JE Term (D44)
    // Produkt-Overflow (perUnit×Metrik → Infinity) abfangen, sonst entstünde später
    // via `Infinity × menge=0` ein stilles NaN (Codex-P0, D44).
    if (!Number.isFinite(c)) throw new PricingError(`${tag}: Kosten-Überlauf (perUnit×Metrik nicht endlich, D44).`);
    sum += c;
  }
  return sum;
}

/**
 * Fan-out-„menge" STRIKT: bei `node.fanOut` MUSS eine eingefrorene, GANZZAHLIGE
 * Kardinalität ≥0 existieren (D34/D42). Nicht-ganzzahlig ⇒ wirft (kein stilles trunc,
 * Codex-P1) — buildPipeline friert die Collection-Länge als Integer-Literal ein.
 */
function fanOutCountStrict(node: PipelineNode): number {
  const n = firstNumericParam(node.params ?? {}, FANOUT_COUNT_KEYS);
  if (n === undefined || !Number.isInteger(n) || n < 0)
    throw new PricingError(
      `${node.id}: fanOut ohne eingefrorene ganzzahlige Kardinalität ≥0 (${FANOUT_COUNT_KEYS.join("/")}) — ` +
        `buildPipeline muss die menge als Integer einfrieren (D34/D42).`,
    );
  return n;
}

/**
 * Effektive Fan-out-menge eines Knotens (§3.6: `× (fanOut ? menge : 1)`).
 * Nicht-Fan-out-Knoten ⇒ IMMER 1 (Override irrelevant, Codex-P1). Fan-out-Knoten ⇒
 * Override (ganzzahlig ≥0, sonst fail-closed) ODER die eingefrorene Kardinalität.
 */
function fanOutCountChecked(node: PipelineNode, override: ((n: PipelineNode) => number) | undefined): number {
  if (!node.fanOut) return 1;
  if (!override) return fanOutCountStrict(node);
  const m = override(node);
  if (!Number.isInteger(m) || m < 0)
    throw new PricingError(`${node.id}: Override-Fan-out-menge ${m} ungültig (ganzzahlig ≥0, D42).`);
  return m;
}

/**
 * Worst-Case-Credit-Estimate für einen aufgelösten Plan (§3.6). Liefert eine
 * `CreditEstimate` (credits + breakdown). `credits` ist garantiert ganzzahlig.
 * Wirft `PricingError` bei jeder Lage, die zu Hold-Unterdeckung führen würde.
 */
export function price(plan: PricedPlan, book: PriceBook, opts: PriceOptions = {}): CreditEstimate {
  const breakdown: CreditEstimate["breakdown"] = [];
  let total = 0;

  // Tool-Basisgebühr (nur wenn im Price-Book deklariert).
  if (Object.prototype.hasOwnProperty.call(book.toolBaseFee, plan.toolId)) {
    const raw = book.toolBaseFee[plan.toolId];
    // §3.6: toolBaseFee ist Record<string, number>. JSONB-`null`/Nicht-Number ⇒ fail-closed
    // (kein `?? 0`-Schlucken, Codex-P2).
    if (typeof raw !== "number" || !nonNegFinite(raw))
      throw new PricingError(`${plan.toolId}: ungültige Basisgebühr ${raw} (muss number, endlich, ≥0 sein, D44).`);
    const base = Math.ceil(raw);
    total += base;
    breakdown.push({ label: `${plan.toolId} · Basisgebühr`, credits: base });
  }

  for (const node of plan.spec.nodes) {
    const tag = `${node.id} (${node.capabilityId}@${node.capabilityVersion})`;
    const params = node.params ?? {};
    // D101: provider-bewusster Preis-Key — `providerId` ist (wenn vorhanden) ein vom Build
    // EINGEFRORENES Literal (D34). FAIL-CLOSED (Codex-P1, wie der `when`-Param-Check): ein
    // vorhandener, aber nicht-literaler providerId (unaufgelöste ScalarRef/leerer String)
    // würde sonst still zum BILLIGEREN generischen Key preisen, während die Laufzeit einen
    // teureren Provider auflösen könnte → Hold-Unterdeckung. Alle realen Builds frieren
    // String-Literale ein; alles andere ist ein nicht eingefrorener Plan → werfen.
    const rawProvider = params["providerId"];
    let pinnedProvider: string | undefined;
    if (rawProvider !== undefined) {
      if (typeof rawProvider !== "string" || rawProvider === "")
        throw new PricingError(
          `${tag}: providerId-Param ist kein nicht-leerer String-Literal (unaufgelöste ScalarRef? ` +
            `Plan nicht eingefroren, D34/D101) — provider-bewusster Preis-Key unbestimmbar.`,
        );
      pinnedProvider = rawProvider;
    }
    const rule = resolveCostRule(book, node.capabilityId, node.capabilityVersion, pinnedProvider);
    if (rule === undefined) {
      // fail-closed (D44): unbekannte Capability-Kosten = Konfigurationsfehler → werfen.
      // `onUnpriced:'zero'` nur, wenn der Aufrufer fehlende Einträge bewusst zulässt.
      if (opts.onUnpriced !== "zero")
        throw new PricingError(
          `${tag}: keine CostRule im Price-Book v${book.version} — Hold-Unterdeckung verhindert (D17/D44). ` +
            `Seed muss jede Capability bepreisen (auch Platzhalter); free-Tools explizit mit 0 (§13).`,
        );
      breakdown.push({ label: `${tag} · unbepreist (§13)`, credits: 0 });
      continue;
    }
    const override = opts.metricsFor?.(node);
    const perElement = evalCostRule(rule, params, override, tag);
    const menge = fanOutCountChecked(node, opts.fanOutCountFor);
    const credits = perElement * menge;
    if (!Number.isSafeInteger(credits))
      throw new PricingError(`${tag}: Knoten-Kosten ${credits} kein sicherer Integer (Overflow/NaN, D44).`);
    total += credits;
    breakdown.push({ label: menge > 1 ? `${tag} ×${menge}` : tag, credits });
  }

  if (!Number.isSafeInteger(total))
    throw new PricingError(`Gesamt-Estimate ${total} kein sicherer Integer (Overflow, D44).`);
  return { credits: total, breakdown };
}
